import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import { TemplateIcon } from '../utils/iconResolver';
import { fetchWithOrg } from '../utils/api';
import { useOrganization } from '../context/OrganizationContext';
import { useToast } from '../hooks/use-toast';
import { usePlan } from '../context/PlanContext';
import {
  ChartBar,
  ChatText,
  CurrencyDollar,
  FileText,
  Funnel,
  Lightning,
  MagnifyingGlass,
  Phone,
  Sparkle,
  TrendUp,
  UsersThree
} from '@phosphor-icons/react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';

const CATEGORIES = [
  { value: '', label: 'All Categories', icon: Sparkle },
  { value: 'sales', label: 'Sales', icon: TrendUp },
  { value: 'customer_service', label: 'Customer Service', icon: ChatText },
  { value: 'operations', label: 'Operations', icon: Lightning },
  { value: 'marketing', label: 'Marketing', icon: TrendUp },
  { value: 'hr', label: 'HR & People', icon: UsersThree },
  { value: 'finance', label: 'Finance', icon: CurrencyDollar },
  { value: 'legal', label: 'Legal & Compliance', icon: FileText },
  { value: 'content', label: 'Content', icon: FileText },
  { value: 'analytics', label: 'Analytics', icon: ChartBar },
  { value: 'ai_ops', label: 'AI Ops', icon: Sparkle },
  { value: 'voice', label: 'Voice', icon: Phone },
];

export default function AgentTemplatesLibrary() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { planLimits } = usePlan();
  const { activeOrganization } = useOrganization();
  
  const [templates, setTemplates] = useState([]);
  const [featuredTemplates, setFeaturedTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    is_voice: null
  });
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [instantiating, setInstantiating] = useState(false);

  useEffect(() => {
    loadTemplates();
    loadFeaturedTemplates();
  }, [filters]);

  const loadTemplates = async () => {
    if (!activeOrganization?.organization_id) return;
    
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.category) params.append('category', filters.category);
      if (filters.is_voice !== null) params.append('is_voice', filters.is_voice);
      
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/agent-templates?${params.toString()}`,
        {},
        activeOrganization.organization_id
      );
      
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
      toast({
        title: 'Error',
        description: 'Failed to load agent templates',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadFeaturedTemplates = async () => {
    if (!activeOrganization?.organization_id) return;
    
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/agent-templates?featured_only=true&page_size=6`,
        {},
        activeOrganization.organization_id
      );
      
      if (response.ok) {
        const data = await response.json();
        setFeaturedTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Failed to load featured templates:', error);
    }
  };

  const handleTemplateClick = (template) => {
    setSelectedTemplate(template);
    setIsDetailOpen(true);
  };

  const handleUseTemplate = async () => {
    if (!selectedTemplate || !activeOrganization?.organization_id) return;

    // Check plan limits
    const maxAgents = planLimits?.max_agents || 0;
    if (maxAgents === 0) {
      toast({
        title: 'Upgrade Required',
        description: 'Your current plan does not allow creating agents. Please upgrade to Pro or Partner Pro.',
        variant: 'destructive'
      });
      return;
    }

    try {
      setInstantiating(true);
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/agent-templates/${selectedTemplate.id}/instantiate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ customize: {} })
        },
        activeOrganization.organization_id
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to create agent' }));
        throw new Error(errorData.detail || 'Failed to create agent');
      }

      const data = await response.json();
      
      if (data.success) {
        toast({
          title: 'Success!',
          description: data.message || 'Agent created successfully',
        });
        
        // Navigate to agent detail page for activation/payment flow
        navigate(data.redirect_url || `/partner-pro/ai-workforce/agents/${data.agent_id}`);
      }
    } catch (error) {
      console.error('Failed to instantiate template:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to create agent from template',
        variant: 'destructive'
      });
    } finally {
      setInstantiating(false);
      setIsDetailOpen(false);
    }
  };

  const getCategoryIcon = (category) => {
    const cat = CATEGORIES.find(c => c.value === category);
    return cat?.icon || Sparkle;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
            Agent Marketplace
          </h1>
          <p className="text-slate-400 text-lg">
            Browse and install pre-built AI agents — ready to work in minutes
          </p>
        </div>

        {/* Featured Templates Row */}
        {featuredTemplates.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Sparkle className="w-5 h-5 text-yellow-400" />
              Featured Templates
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {featuredTemplates.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  onClick={() => handleTemplateClick(template)}
                  featured
                />
              ))}
            </div>
          </div>
        )}

        {/* MagnifyingGlass and Filters */}
        <div className="mb-6 space-y-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* MagnifyingGlass */}
            <div className="flex-1 relative">
              <MagnifyingGlass className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search templates..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="w-full pl-10 pr-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-white placeholder-slate-400"
              />
            </div>

            {/* Category Funnel */}
            <select
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value })}
              className="px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-white"
            >
              {CATEGORIES.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>

            {/* Voice Funnel */}
            <select
              value={filters.is_voice === null ? '' : filters.is_voice.toString()}
              onChange={(e) => setFilters({ ...filters, is_voice: e.target.value === '' ? null : e.target.value === 'true' })}
              className="px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-white"
            >
              <option value="">All Types</option>
              <option value="true">Voice Agents</option>
              <option value="false">Text Agents</option>
            </select>
          </div>
        </div>

        {/* Templates Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
            <p className="mt-4 text-slate-400">Loading templates...</p>
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No templates found matching your filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                onClick={() => handleTemplateClick(template)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Template Detail Drawer */}
      <Sheet open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <SheetContent className="w-full sm:max-w-xl bg-slate-900 border-slate-800 overflow-y-auto pt-20">
          {selectedTemplate && (
            <>
              <SheetHeader className="pt-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 rounded-xl bg-[rgba(99,102,241,0.15)] flex items-center justify-center"><TemplateIcon icon={selectedTemplate.icon} size={28} className="text-indigo-400" /></div>
                    <div>
                      <SheetTitle className="text-2xl text-white">
                        {selectedTemplate.name}
                      </SheetTitle>
                      <p className="text-sm text-slate-400 mt-1">
                        {selectedTemplate.tagline}
                      </p>
                    </div>
                  </div>
                </div>
              </SheetHeader>

              <div className="mt-6 space-y-6 pb-8">
                {/* Category and Tags */}
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm capitalize">
                    {selectedTemplate.category.replace('_', ' ')}
                  </span>
                  {selectedTemplate.is_voice_template && (
                    <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-sm flex items-center gap-1">
                      <Phone className="w-3 h-3" />
                      Voice Agent
                    </span>
                  )}
                  {selectedTemplate.tags?.slice(0, 3).map((tag) => (
                    <span key={tag} className="px-3 py-1 bg-slate-700/50 text-slate-300 rounded-full text-sm">
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Description */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">About</h3>
                  <p className="text-slate-300 leading-relaxed">
                    {selectedTemplate.description}
                  </p>
                </div>

                {/* Use Cases */}
                {selectedTemplate.use_cases?.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Common Use Cases</h3>
                    <ul className="space-y-2">
                      {selectedTemplate.use_cases.map((useCase, index) => (
                        <li key={index} className="flex items-start gap-2 text-slate-300">
                          <span className="text-purple-400 mt-1">•</span>
                          {useCase}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Setup Info */}
                <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                  <div>
                    <p className="text-sm text-slate-400">Difficulty</p>
                    <p className="text-white font-medium capitalize">{selectedTemplate.difficulty_level}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Setup Time</p>
                    <p className="text-white font-medium">{selectedTemplate.estimated_setup_time || 'Quick'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Used</p>
                    <p className="text-white font-medium">{selectedTemplate.usage_count || 0}×</p>
                  </div>
                </div>

                {/* Action Button */}
                <button
                  onClick={handleUseTemplate}
                  disabled={instantiating}
                  className="w-full py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
                >
                  {instantiating ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Creating Agent...
                    </>
                  ) : (
                    <>
                      <Lightning className="w-5 h-5" />
                      Install Agent
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

// Template Card Component
function TemplateCard({ template, onClick, featured = false }) {
  const Icon = getCategoryIconForCard(template.category);
  
  // Check if template is new (created in last 14 days)
  const isNew = template.created_at && 
    (new Date() - new Date(template.created_at)) < (14 * 24 * 60 * 60 * 1000);
  
  // Check if template is popular (popularity_score >= 80)
  const isPopular = template.popularity_score >= 80;
  
  return (
    <div
      onClick={onClick}
      className={`relative group cursor-pointer bg-gradient-to-br from-slate-800/50 to-slate-900/50 border hover:border-purple-500/50 transition-all duration-200 rounded-xl p-6 hover:shadow-lg hover:shadow-purple-500/10 ${
        featured ? 'border-purple-500/30' : 'border-slate-700'
      }`}
    >
      <div className="absolute top-3 right-3 flex gap-2">
        {isPopular && !isNew && (
          <span className="px-2 py-1 bg-gradient-to-r from-orange-500 to-amber-500 text-white text-xs font-bold rounded-full flex items-center gap-1">
            <TrendUp className="w-3 h-3" />
            POPULAR
          </span>
        )}
        {isNew && (
          <span className="px-2 py-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs font-bold rounded-full">
            NEW
          </span>
        )}
        {featured && (
          <Sparkle className="w-5 h-5 text-yellow-400" />
        )}
      </div>
      
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-[rgba(99,102,241,0.15)] flex items-center justify-center flex-shrink-0"><TemplateIcon icon={template.icon} size={24} className="text-indigo-400" /></div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-white mb-1 group-hover:text-purple-400 transition-colors line-clamp-1">
            {template.name}
          </h3>
          <p className="text-sm text-slate-400 mb-3 line-clamp-2">
            {template.tagline || template.description}
          </p>
          
          <div className="flex flex-wrap gap-2 mb-3">
            <span className="px-2 py-1 bg-slate-700/50 text-slate-300 rounded text-xs capitalize">
              {template.category.replace('_', ' ')}
            </span>
            {template.is_voice_template && (
              <span className="px-2 py-1 bg-green-500/20 text-green-300 rounded text-xs flex items-center gap-1">
                <Phone className="w-3 h-3" />
                Voice
              </span>
            )}
          </div>
          
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span className="capitalize">{template.difficulty_level}</span>
            <span>{template.estimated_setup_time || '10 min'}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function getCategoryIconForCard(category) {
  const icons = {
    sales: TrendUp,
    customer_service: ChatText,
    operations: Lightning,
    marketing: TrendUp,
    hr: UsersThree,
    finance: CurrencyDollar,
    legal: FileText,
    content: FileText,
    analytics: ChartBar,
    ai_ops: Sparkle,
    voice: Phone,
  };
  return icons[category] || Sparkle;
}
